import { Nav } from "@/components/common/Nav";
import { DetailedMatch, ScoringUpdates, getDetailedMatch } from "@/data/match";
import { ScoringUpdates as DBScoringUdates, Match } from "@prisma/client";
import prisma from "@/helper/client";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { CopyBlock } from "react-code-blocks";
import * as prettier from "prettier";
import { getDate, getTime } from "@/helper/time";
import { sortScoringUpdates, updatesToSerialized } from "@/helper/match";
import { usePatchMatch } from "@/hooks/match";
import { FaFlag } from "react-icons/fa";
import { useEffect } from "react";
import { useRouter } from "next/router";

export async function getServerSideProps(context: any) {
  const id = context.query.id;
  if (!id) {
    return {
      redirect: {
        destination: "/admin/",
        permanent: false,
      },
    };
  }
  const basicMatch = await prisma.match.findFirst({
    where: { id: id as string },
  });
  const match = await getDetailedMatch(id as string);
  const updates = updatesToSerialized(
    await prisma.scoringUpdates.findMany({
      where: { matchId: id },
      orderBy: { ts: "asc" },
    })
  ) as unknown as ScoringUpdates[];
  sortScoringUpdates(updates);

  const validationState =
    match &&
    checkScoringUpdates(
      updates,
      match.games.map((g) => g.homePlayer?.id ?? g.homeDouble?.id ?? ""),
      match.games.map((g) => g.awayPlayer?.id ?? g.awayDouble?.id ?? "")
    );

  const formattedMatch = await prettier.format(JSON.stringify(match), {
    parser: "json",
  });
  const formattedUpdates = await prettier.format(JSON.stringify(updates), {
    parser: "json",
  });
  const formattedValidationState = await prettier.format(
    JSON.stringify(validationState),
    { parser: "json" }
  );

  const lastUpdate = updates.length > 0 ? updates[updates.length - 1] : null;
  const formattedLastUpdate = await prettier.format(
    JSON.stringify(lastUpdate),
    { parser: "json" }
  );

  return {
    props: {
      match: JSON.parse(formattedMatch),
      updateCount: match?.games.reduce(
        (a, b) => a + b.scoringUpdates.length,
        0
      ),
      gameIds: match?.games.map((g) => g.id),
      updates: JSON.parse(formattedUpdates),
      validationState: JSON.parse(formattedValidationState),
      games: match?.games ?? null,
      lastUpdate: JSON.parse(formattedLastUpdate),
      basicMatch,
      ...(await serverSideTranslations(context.locale)),
    },
  };
}

export function GameScheduleDebugPage({
  match,
  updateCount,
  updates,
  gameIds,
  validationState,
  games,
  lastUpdate,
  basicMatch,
}: {
  match: DetailedMatch;
  updateCount: number;
  gameIds: string[];
  updates: ScoringUpdates[];
  validationState: ValidationState | null;
  games: DetailedMatch["games"] | null;
  lastUpdate: ScoringUpdates | null;
  basicMatch: Match;
}) {
  const { mutate: updateMatch, isSuccess: isMatchUpdated } = usePatchMatch();
  const { reload } = useRouter();

  function handleSetFlag() {
    updateMatch({
      ...basicMatch,
      isFlaggedAsBroken: !basicMatch.isFlaggedAsBroken,
    });
  }

  useEffect(() => {
    if (isMatchUpdated) {
      reload();
    }
  }, [isMatchUpdated, reload]);
  return (
    <>
      <Head>
        <title>TTBL Adminsystem | Bundesliga | Debug Info</title>
      </Head>
      <Nav />
      <div className="container">
        <div className="flex justify-between items-center">
          <h3>
            Debug Info: {match.homeTeam?.name} vs {match.awayTeam?.name}
          </h3>
          <div>
            <button
              onClick={handleSetFlag}
              className={`px-4 py-2 rounded-md ${basicMatch.isFlaggedAsBroken ? "bg-red text-white" : "text-black border border-black"}`}
            >
              <FaFlag />
            </button>
          </div>
        </div>
        <div>
          {match.gameday.name} am {getDate(match.timeStamp)} um{" "}
          {getTime(match.timeStamp)} Uhr
        </div>
        <div>
          {`Angewendete Updates: ${
            match.updateCount ?? "Keine Angabe (Alte Datenstruktur)"
          }, Tatsächlich vorhandene: ${updateCount}`}
        </div>
        <div>{`Spiele: ${gameIds.join(", ")}`}</div>
        <div>
          <h3>Aktuelle Daten</h3>
          <h4>Alle Spiele im Frontend</h4>
          {games?.map((game, index) => (
            <details key={game.id}>
              <summary>{`Spiel ${index + 1}: ${game.homePlayer?.lastName ?? `${game.homeDouble?.leaguePlayerOne?.lastName ?? ""} / ${game.homeDouble?.leaguePlayerTwo?.lastName ?? ""}`} vs ${game.awayPlayer?.lastName ?? `${game.awayDouble?.leaguePlayerOne?.lastName ?? ""} / ${game.awayDouble?.leaguePlayerTwo?.lastName ?? ""}`}`}</summary>
              <div>{`Status: ${game.gameState}`}</div>
              <div>{`Sätze: ${game.homeSets} : ${game.awaySets}`}</div>
              {game.set1HomeScore != undefined &&
                game.set1AwayScore != undefined && (
                  <div>{`Satz 1: ${game.set1HomeScore} : ${game.set1AwayScore}`}</div>
                )}
              {game.set2HomeScore != undefined &&
                game.set2AwayScore != undefined && (
                  <div>{`Satz 2: ${game.set2HomeScore} : ${game.set2AwayScore}`}</div>
                )}
              {game.set3HomeScore != undefined &&
                game.set3AwayScore != undefined && (
                  <div>{`Satz 3: ${game.set3HomeScore} : ${game.set3AwayScore}`}</div>
                )}
              {game.set4HomeScore != undefined &&
                game.set4AwayScore != undefined && (
                  <div>{`Satz 4: ${game.set4HomeScore} : ${game.set4AwayScore}`}</div>
                )}
              {game.set5HomeScore != undefined &&
                game.set5AwayScore != undefined && (
                  <div>{`Satz 5: ${game.set5HomeScore} : ${game.set5AwayScore}`}</div>
                )}
            </details>
          ))}
          <div className="mt-4">
            {lastUpdate && (
              <details>
                <summary>Neuestes Event aus dem Backend</summary>
                <CopyBlock
                  text={JSON.stringify(lastUpdate, null, 2)}
                  language="json"
                  showLineNumbers
                  wrapLongLines
                />
              </details>
            )}
          </div>
        </div>
        <div>
          <h3>Updates Validiert</h3>
          <details>
            <summary>Validation State</summary>
            <CopyBlock
              text={JSON.stringify(validationState, null, 2)}
              language="json"
              showLineNumbers
              wrapLongLines
            />
          </details>
        </div>
        <div>
          <h3>Rohdaten</h3>
          <details>
            <summary>Updates</summary>
            <CopyBlock
              text={JSON.stringify(updates, null, 2)}
              language="json"
              showLineNumbers
              wrapLongLines
            />
          </details>
          <details>
            <summary>Match</summary>
            <CopyBlock
              text={JSON.stringify(match, null, 2)}
              language="json"
              showLineNumbers
              wrapLongLines
            />
          </details>
        </div>
      </div>
    </>
  );
}

type ValidationState = {
  currentSet: number | null;
  homePoints: number;
  awayPoints: number;
  homeSets: number;
  awaySets: number;
  homeGames: number;
  awayGames: number;
  gameStarted: boolean;
  setStarted: boolean;
  errors: string[];
  homeSubstitutions: { oldId: string; newId: string; game: string }[];
  awaySubstitutions: { oldId: string; newId: string; game: string }[];
};

function checkScoringUpdates(
  updates: ScoringUpdates[],
  homePlayerIds: string[],
  awayPlayerIds: string[]
): ValidationState {
  const state: ValidationState = {
    currentSet: null,
    homePoints: 0,
    awayPoints: 0,
    homeSets: 0,
    awaySets: 0,
    homeGames: 0,
    awayGames: 0,
    gameStarted: false,
    setStarted: false,
    errors: [],
    homeSubstitutions: [],
    awaySubstitutions: [],
  };
  let lastUpdate: ScoringUpdates | null = null;
  updates.forEach((update) => {
    const gameIndex = state.homeGames + state.awayGames;
    if (lastUpdate && isDuplicate(lastUpdate, update)) {
      state.errors.push(
        `Duplicate update (ID1: ${lastUpdate.id}, ID2: ${update.id}, Game: ${gameIndex + 1})`
      );
    } else {
      switch (update.type) {
        case "EventGameStart":
          if (state.gameStarted) {
            state.errors.push(
              `Game start event with no game end event (ID: ${update.id}, Game: ${
                gameIndex + 1
              })`
            );
          }
          state.gameStarted = true;
          state.homeSets = 0;
          state.awaySets = 0;
          break;
        case "EventSetStart":
          if (state.setStarted) {
            state.errors.push(
              `Set start event with no set end event (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set}, Current Set: ${state.currentSet}, state: ${state.setStarted}`
            );
          }
          if (!state.gameStarted) {
            state.errors.push(
              `Set start event with no game started (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          state.setStarted = true;
          state.homePoints = 0;
          state.awayPoints = 0;
          state.currentSet = update.set || null;
          break;
        case "Point":
          if (!state.setStarted) {
            state.errors.push(
              `Point event with no set started (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (state.currentSet !== update.set) {
            state.errors.push(
              `Point event with wrong set (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (!state.gameStarted) {
            state.errors.push(
              `Point event with no game started (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }

          if (update.playerId === homePlayerIds[gameIndex]) {
            state.homePoints++;
          } else if (update.playerId === awayPlayerIds[gameIndex]) {
            state.awayPoints++;
          } else {
            const homeSub = state.homeSubstitutions.find(
              (sub) => sub.oldId === update.playerId
            );
            const awaySub = state.awaySubstitutions.find(
              (sub) => sub.oldId === update.playerId
            );
            if (homeSub) {
              state.homePoints++;
              state.errors.push(
                `Point event uses id of substituted home player (ID: ${update.id}, Game: ${
                  gameIndex + 1
                }, Set: ${update.set}), expected ${homePlayerIds[gameIndex]}, got ${update.playerId}`
              );
            } else if (awaySub) {
              state.awayPoints++;
              state.errors.push(
                `Point event uses id of substituted away player (ID: ${update.id}, Game: ${
                  gameIndex + 1
                }, Set: ${update.set}), expected ${awayPlayerIds[gameIndex]}, got ${update.playerId}`
              );
            } else {
              state.errors.push(
                `Point event with invalid player (ID: ${update.id}, Game: ${
                  gameIndex + 1
                }, Set: ${update.set}), Player ${update.playerId}`
              );
            }
          }
          if (`${state.homePoints}:${state.awayPoints}` !== update.result) {
            state.errors.push(
              `Point event with invalid result (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set}), expected ${state.homePoints}:${
                state.awayPoints
              }, got ${update.result}`
            );
            if (update.result) {
              const [homePoints, awayPoints] = update.result
                .split(":")
                .map(Number);
              state.homePoints = homePoints;
              state.awayPoints = awayPoints;
            }
          }

          break;
        //Not used Anymore, Undo is used instead
        case "DecrementPoint":
          if (!state.setStarted) {
            state.errors.push(
              `Decrement point event with no set started (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (state.currentSet !== update.set) {
            state.errors.push(
              `Decrement point event with wrong set (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (!state.gameStarted) {
            state.errors.push(
              `Decrement point event with no game started (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (state.homePoints === 0 && state.awayPoints === 0) {
            state.errors.push(
              `Decrement point event with no points to decrement (ID: ${
                update.id
              }, Game: ${gameIndex + 1}, Set: ${update.set})`
            );
          }
          if (update.playerId === homePlayerIds[gameIndex]) {
            state.homePoints--;
          } else if (update.playerId === awayPlayerIds[gameIndex]) {
            state.awayPoints--;
          } else {
            state.errors.push(
              `Decrement point event with invalid player (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (`${state.homePoints}:${state.awayPoints}` !== update.result) {
            state.errors.push(
              `Decrement point event with invalid result (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set}), expected ${state.homePoints}:${
                state.awayPoints
              }, got ${update.result}`
            );
            if (update.result) {
              const [homePoints, awayPoints] = update.result
                .split(":")
                .map(Number);
              state.homePoints = homePoints;
              state.awayPoints = awayPoints;
            }
          }
          break;
        case "EventSetEnd":
          if (!state.setStarted) {
            state.errors.push(
              `Set end event with no set start event (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (
            (state.homePoints >= 11 || state.awayPoints >= 11) &&
            Math.abs(state.homePoints - state.awayPoints) >= 2
          ) {
            if (state.homePoints > state.awayPoints) {
              state.homeSets++;
            } else {
              state.awaySets++;
            }
          } else {
            state.errors.push(
              `Invalid set end with incomplete scoring (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          }
          if (update.set && state.currentSet && update.set < state.currentSet) {
            state.errors.push(
              `Set end and set started are switched (ID: ${update.id}, Game: ${
                gameIndex + 1
              }, Set: ${update.set})`
            );
          } else {
            state.setStarted = false;
          }
          break;
        case "EventGameEnd":
          if (!state.gameStarted) {
            state.errors.push(
              `Game end event with no game started (ID: ${update.id}, Game: ${
                gameIndex + 1
              })`
            );
          }
          if (state.setStarted) {
            state.errors.push(
              `Game end event with no set end event (ID: ${update.id}, Game: ${
                gameIndex + 1
              })`
            );
          }
          if (state.homeSets >= 3 || state.awaySets >= 3) {
            if (state.homeSets > state.awaySets) {
              state.homeGames++;
            } else {
              state.awayGames++;
            }
            state.gameStarted = false;
          } else {
            state.errors.push(
              `Invalid game end with incomplete scoring (ID: ${
                update.id
              }, Game: ${gameIndex + 1})`
            );
          }
          break;
        case "Substitution":
        case "SubstitutionInjury":
          if (update.homePlayerId) {
            state.homeSubstitutions.push({
              oldId: homePlayerIds[gameIndex],
              newId: update.homePlayerId,
              game: update.gameId,
            });
            homePlayerIds[gameIndex] = update.homePlayerId;
          } else if (update.awayPlayerId) {
            state.awaySubstitutions.push({
              oldId: awayPlayerIds[gameIndex],
              newId: update.awayPlayerId,
              game: update.gameId,
            });
            awayPlayerIds[gameIndex] = update.awayPlayerId;
          } else {
            state.errors.push(
              `Substitution event with invalid player (ID: ${update.id}, Game: ${
                gameIndex + 1
              })`
            );
          }
          break;
        default:
          break;
      }
    }
    lastUpdate = update;
  });
  return state;
}

export function isDuplicate(
  event1: ScoringUpdates | DBScoringUdates,
  event2: ScoringUpdates | DBScoringUdates
) {
  return (
    event1.matchId === event2.matchId &&
    event1.gameId === event2.gameId &&
    event1.type === event2.type &&
    event1.playerId === event2.playerId &&
    event1.set === event2.set &&
    event1.result === event2.result &&
    event1.isLuckyShot === event2.isLuckyShot &&
    event1.homePlayerId === event2.homePlayerId &&
    event1.awayPlayerId === event2.awayPlayerId
  );
}

export default GameScheduleDebugPage;
